/**
 * Off-screen host for decode/canvas pipelines.
 * - Android: keep a tiny but paintable node (fully off-screen media can stall decode).
 * - Safari / desktop: keep a real-size node moved off-screen (1×1 / 16×16 can stall WebKit decode).
 */
let captureHostRoot: HTMLDivElement | null = null

const ANDROID_HOST_STYLE =
  'position:fixed;left:0;top:0;width:1px;height:1px;overflow:hidden;opacity:1;pointer-events:none;z-index:2147483647;'

const ANDROID_VIDEO_STYLE =
  'position:absolute;left:0;top:0;width:16px;height:16px;opacity:1;pointer-events:none;object-fit:cover;'

const ANDROID_CANVAS_STYLE =
  'position:absolute;left:0;top:0;width:1px;height:1px;opacity:1;pointer-events:none;'

const DESKTOP_HOST_STYLE =
  'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;pointer-events:none;z-index:-1;'

const DESKTOP_VIDEO_STYLE =
  'position:fixed;left:-10000px;top:0;width:640px;height:480px;opacity:1;pointer-events:none;z-index:-1;'

function isAndroidCaptureRuntime(): boolean {
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)
}

function getCaptureHost(): HTMLDivElement {
  if (!captureHostRoot) {
    captureHostRoot = document.createElement('div')
    captureHostRoot.setAttribute('data-xmax-capture-host', 'true')
    captureHostRoot.style.cssText = isAndroidCaptureRuntime()
      ? ANDROID_HOST_STYLE
      : DESKTOP_HOST_STYLE
    document.body.appendChild(captureHostRoot)
  }
  return captureHostRoot
}

export function mountCaptureVideo(videoEl: HTMLVideoElement): void {
  const android = isAndroidCaptureRuntime()
  videoEl.setAttribute('playsinline', 'true')
  videoEl.setAttribute('webkit-playsinline', 'true')
  if (android) {
    videoEl.setAttribute('x5-playsinline', 'true')
    videoEl.setAttribute('x5-video-player-type', 'h5')
    videoEl.setAttribute('x5-video-player-fullscreen', 'false')
    videoEl.setAttribute('muted', '')
    videoEl.setAttribute('preload', 'auto')
  }
  videoEl.style.cssText = android ? ANDROID_VIDEO_STYLE : DESKTOP_VIDEO_STYLE
  getCaptureHost().appendChild(videoEl)
}

/** Android may need the capture canvas in the DOM; Safari/desktop do not. */
export function mountCaptureCanvas(canvasEl: HTMLCanvasElement): void {
  if (!isAndroidCaptureRuntime()) {
    return
  }
  canvasEl.style.cssText = ANDROID_CANVAS_STYLE
  getCaptureHost().appendChild(canvasEl)
}
